import { useEffect, useState, useCallback } from 'react'
import { useRecoilCallback, useRecoilValue, useSetRecoilState } from 'recoil'
import {
  editorPagesAtom,
  baseLayoutAtom,
  baseLayoutMarginsSelector,
  canvasPageIdAtom,
  itemAtomFamily,
  itemIdAtom,
} from '@/atoms/_editorAtoms'
import {
  bleedWarningsAtom,
  bleedCheckCompletedAtom,
} from '@/atoms/bleedWarningAtoms'
import {
  editorCanvasTextBoxFramesSelector,
  editorPageContentAtomFamily,
  editorTextBoxDraftsAtom,
  editorTextBoxPendingDeletesAtom,
  editorTextBoxResolveInputsSelector,
} from '@/atoms/editorTextBoxAtoms'
import {
  EDITOR_PAGE_CONTENT_STALL_TIMEOUT_MS,
  getEditorPageContent,
} from '@/api/editorPageContent'
import { bleedFoldMarginsFor, checkBleedOverlap } from '@messenger/utils'
import type { FoldLayoutSource } from '@messenger/utils'
import { countEditorProbeRender } from '@/utils/editor/editorProbe'
import { measureEditorTextBoxInkRect } from '@/utils/editor/measureEditorTextBoxInkBounds'
import {
  EDITOR_TEXT_BOX_ITEM_TYPE,
  editorTextBoxGeometryDraftUpdates,
  editorTextBoxViewModelResults,
  editorTextBoxWithItemGeometry,
  isEditorTextBoxItem,
  isEmptyEditorTextBoxDocument,
  isPlaceholderOnlyEditorTextBoxDocument,
} from '@/utils/editor-text-box/editorTextBoxAdapter'
import type {
  EditorInkRect,
  EditorPageContentBundle,
  EditorTextBoxDocument,
} from '@/utils/editor-text-box/editorTextBoxAdapter'
import type { Content } from '@messenger/types'

export type BleedWarningInfo = {
  itemId: string
  itemType: typeof EDITOR_TEXT_BOX_ITEM_TYPE
  pageNumber: number
  bleedType: 'edge' | 'center'
  location: string
}

type Bleeds = Parameters<typeof checkBleedOverlap>[4]

type CheckableRect = {
  x: number
  y: number
  width: number
  height: number
}

export const bleedWarningForRect = (
  itemId: string,
  itemType: BleedWarningInfo['itemType'],
  rect: CheckableRect,
  pageIndex: number,
  page: Content,
  bleeds: Bleeds,
  /** The product's raw layout: a trifold is judged against this page's own
   * folds, and any other product keeps its center margins. */
  layout: FoldLayoutSource | null,
): BleedWarningInfo | null => {
  const pageWidth = page?.product?.productSize?.width ?? 0
  const pageHeight = page?.product?.productSize?.height ?? 0
  const padding = page?.product?.padding ?? null
  const overlapResult = checkBleedOverlap(
    rect.x,
    rect.y,
    rect.width,
    rect.height,
    bleeds,
    pageWidth,
    pageHeight,
    padding,
    // Folds are placed by the page's JSON number, as the canvas draws them.
    // A page with no JSON number, which the canvas draws no folds for, is
    // judged at its position instead.
    bleedFoldMarginsFor(
      layout,
      page?.pageProperties?.pageNumber ?? pageIndex + 1,
      pageWidth,
    ),
  )
  if (!overlapResult.isOverlapping) return null
  return {
    itemId: `${itemType}-${itemId}`,
    itemType,
    pageNumber: pageIndex + 1,
    bleedType: overlapResult.bleedType!,
    location: overlapResult.location!,
  }
}

/**
 * The rect the bleed check compares for a text box: its ink, offset by the
 * frame's position. A box with no ink has nothing to judge.
 */
export const editorTextBoxCheckRect = (
  document: EditorTextBoxDocument,
  ink: EditorInkRect | null,
): CheckableRect | null => {
  if (!ink) return null
  const box = document.box
  return {
    x: box.x + ink.left,
    y: box.y + ink.top,
    width: Math.max(0, ink.right - ink.left),
    height: Math.max(0, ink.bottom - ink.top),
  }
}

// One empty verdict, so a check that keeps finding nothing hands its setters
// the same reference and they skip the update.
const NO_WARNINGS: BleedWarningInfo[] = []

type BleedCheckResults = {
  warnings: BleedWarningInfo[]
  isProcessing: boolean
  processedCount: number
  totalCount: number
  progressPercentage: number
  recheckAll: () => void
}

type UseAllPagesBleedCheckArgs = {
  enabled?: boolean
  debounceMs?: number
}

const pageIdOf = (page: Content): string | null => {
  const id = (page?.product as { orderPageId?: unknown } | undefined)
    ?.orderPageId
  return typeof id === 'string' && id ? id : null
}

/** The page a draft belongs to: whichever id its anchor names. */
const anchorPageIdOf = (
  anchor: EditorTextBoxDocument['anchor'],
): string | null => {
  const named = anchor as Partial<
    Record<
      'orderPageId' | 'm3OrderPageId' | 'globalPageId' | 'funeralHomePageId',
      string
    >
  >
  return (
    named.orderPageId ??
    named.m3OrderPageId ??
    named.globalPageId ??
    named.funeralHomePageId ??
    null
  )
}

/** A page the whole-product check reads, and where it sits in the product. */
export type BleedCheckPage = {
  page: Content
  pageIndex: number
  pageId: string
}

type CanvasItems = Parameters<
  typeof editorTextBoxGeometryDraftUpdates
>[0]['items']

/** The page the canvas shows, and the items on it. */
export type BleedCheckCanvas = {
  pageId: string | null
  items: CanvasItems
}

const NO_CANVAS: BleedCheckCanvas = { pageId: null, items: [] }

/**
 * The pages the whole-product check reads, and how many boxes it expects to
 * judge. A page is read when its saved items hold a Lotus text box, when a
 * draft is anchored to it, or when it is on the canvas and the canvas holds
 * one. A box added since the last Save reaches the page's items only when a
 * Save writes them: a box typed into has a draft by then, and one pasted or
 * inserted from a service template is on the canvas alone until it is
 * edited. A box counts once, wherever it is found. A page with no id cannot
 * be read.
 */
export const bleedCheckPages = (
  pages: readonly Content[],
  drafts: Readonly<Record<string, EditorTextBoxDocument>>,
  canvas: BleedCheckCanvas = NO_CANVAS,
): { pages: BleedCheckPage[]; boxCount: number } => {
  const draftedBoxIds = new Map<string, Set<string>>()
  for (const [boxId, draft] of Object.entries(drafts)) {
    const pageId =
      draft.anchor && draft.box ? anchorPageIdOf(draft.anchor) : null
    if (pageId !== null)
      draftedBoxIds.set(
        pageId,
        (draftedBoxIds.get(pageId) ?? new Set<string>()).add(boxId),
      )
  }
  const walked: BleedCheckPage[] = []
  let boxCount = 0
  pages.forEach((page, pageIndex) => {
    const pageId = pageIdOf(page)
    if (pageId === null) return
    const boxIds = new Set(draftedBoxIds.get(pageId))
    const onCanvas = pageId === canvas.pageId ? canvas.items : []
    for (const item of [...(page?.product?.items ?? []), ...onCanvas])
      if (isEditorTextBoxItem(item)) boxIds.add(String(item.id))
    if (boxIds.size === 0) return
    walked.push({ page, pageIndex, pageId })
    boxCount += boxIds.size
  })
  return { pages: walked, boxCount }
}

// The document a walk judged for a box the canvas moved since the last Save,
// by the draft or saved document it was folded from. A fold builds a new
// document, and the ink measure remembers a box by its document, so folding
// the same frame again on every walk would render the box offscreen on every
// walk.
const foldedFrom = new WeakMap<EditorTextBoxDocument, EditorTextBoxDocument>()

/**
 * The canvas's frames folded into the boxes it moved, resized or turned
 * since the last Save, the way the Save folds them, with nothing written. A
 * box whose frame is the one it had at its last fold keeps that fold's
 * document.
 */
const canvasFolds = ({
  items,
  saved,
  drafts,
  pendingDeleteIds,
}: {
  items: CanvasItems
  saved: ReadonlyMap<string, EditorTextBoxDocument>
  drafts: Readonly<Record<string, EditorTextBoxDocument>>
  pendingDeleteIds: ReadonlySet<string>
}): Record<string, EditorTextBoxDocument> => {
  const baseOf = (boxId: string): EditorTextBoxDocument | undefined =>
    drafts[boxId] ?? saved.get(boxId)
  const kept: Record<string, EditorTextBoxDocument> = {}
  const toFold = items.filter((item) => {
    if (typeof item.id !== 'string') return true
    const base = baseOf(item.id)
    const previous = base === undefined ? undefined : foldedFrom.get(base)
    if (
      previous === undefined ||
      editorTextBoxWithItemGeometry(previous.box, item) !== previous.box
    )
      return true
    kept[item.id] = previous
    return false
  })
  const folded = editorTextBoxGeometryDraftUpdates({
    items: toFold,
    results: new Map(
      [...saved].map(([boxId, document]) => [boxId, { document }]),
    ),
    drafts,
    pendingDeleteIds,
  })
  for (const [boxId, document] of Object.entries(folded)) {
    const base = baseOf(boxId)
    if (base !== undefined) foldedFrom.set(base, document)
  }
  return { ...kept, ...folded }
}

/**
 * The boxes the check judges on one page: its saved documents, each replaced
 * by its draft when one is anchored to this page, less the boxes deleted but
 * not yet saved. For the page on the canvas, `canvasItems` are its items: a
 * box moved, resized or turned there since the last Save holds its new frame
 * on its item alone until a Save folds it into the draft, so the frame is
 * folded in here the way the Save folds it, and nothing is written. An empty
 * box and one holding only the placeholder are not judged; a locked box is
 * judged like any other.
 */
export const bleedCheckPageDocuments = ({
  pageId,
  saved,
  drafts,
  pendingDeletes,
  canvasItems = [],
}: {
  pageId: string
  saved: ReadonlyMap<string, EditorTextBoxDocument>
  drafts: Readonly<Record<string, EditorTextBoxDocument>>
  pendingDeletes: Readonly<Record<string, unknown>>
  canvasItems?: CanvasItems
}): EditorTextBoxDocument[] => {
  const pendingDeleteIds = new Set(Object.keys(pendingDeletes))
  const live =
    canvasItems.length === 0
      ? drafts
      : {
          ...drafts,
          ...canvasFolds({
            items: canvasItems,
            saved,
            drafts,
            pendingDeleteIds,
          }),
        }
  const byId = new Map(saved)
  // The canvas page's drafts are the live truth for that page alone; a draft
  // judged against another page's frame would warn wrongly.
  for (const [boxId, draft] of Object.entries(live))
    if (draft.anchor && draft.box && anchorPageIdOf(draft.anchor) === pageId)
      byId.set(boxId, draft)
  for (const boxId of pendingDeleteIds) byId.delete(boxId)
  return [...byId.values()].filter(
    (document) =>
      !isEmptyEditorTextBoxDocument(document) &&
      !isPlaceholderOnlyEditorTextBoxDocument(document),
  )
}

/**
 * A page's content, read a second time when the first read fails, or null
 * when the second fails too. A page the walk cannot read has no verdict, and
 * judging it clean would let Back past a box that bleeds.
 */
const loadWithOneRetry = async (
  load: (pageId: string) => Promise<EditorPageContentBundle>,
  pageId: string,
): Promise<EditorPageContentBundle | null> => {
  try {
    return await load(pageId)
  } catch {
    // One failed read may be a passing fault; the second read answers for it.
  }
  try {
    return await load(pageId)
  } catch (error) {
    console.warn(
      `The bleed check could not read page ${pageId} after a retry and gives no verdict:`,
      error instanceof Error ? error.message : String(error),
    )
    return null
  }
}

export const useAllPagesBleedCheck = ({
  enabled = true,
  debounceMs = 500,
}: UseAllPagesBleedCheckArgs = {}): BleedCheckResults => {
  const editorPages = useRecoilValue(editorPagesAtom) as Content[]
  const bleeds = useRecoilValue(baseLayoutMarginsSelector)
  const layout = useRecoilValue(baseLayoutAtom)
  const drafts = useRecoilValue(editorTextBoxDraftsAtom)
  const pendingDeletes = useRecoilValue(editorTextBoxPendingDeletesAtom)
  // A paste or a move on the canvas changes no draft until a Save; this is
  // what tells the walk that a box on the canvas page appeared, moved,
  // resized or turned.
  const canvasFrames = useRecoilValue(editorCanvasTextBoxFramesSelector)
  const setBleedWarnings = useSetRecoilState(bleedWarningsAtom)
  const setBleedCheckCompleted = useSetRecoilState(bleedCheckCompletedAtom)

  const [warnings, setWarnings] = useState<BleedWarningInfo[]>([])
  const [isProcessing, setIsProcessing] = useState(false)
  const [processedCount, setProcessedCount] = useState(0)
  const [totalCount, setTotalCount] = useState(0)
  const [recheckTrigger, setRecheckTrigger] = useState(0)

  const recheckAll = useCallback(() => {
    setRecheckTrigger((prev) => prev + 1)
  }, [])

  const loadPageBundle = useRecoilCallback(
    ({ snapshot, set }) =>
      async (pageId: string): Promise<EditorPageContentBundle> => {
        const cached = await snapshot.getPromise(
          editorPageContentAtomFamily(pageId),
        )
        if (cached !== null) return cached
        const { data } = await getEditorPageContent(pageId, {
          timeout: EDITOR_PAGE_CONTENT_STALL_TIMEOUT_MS,
        })
        // Whatever filled the atom while this read was out (the canvas's own
        // load, or an insert, paste or template writing its new box) keeps
        // it: this read may predate that write and would drop the new box.
        set(editorPageContentAtomFamily(pageId), (current) => current ?? data)
        return data
      },
    [],
  )

  // The page the canvas shows and its items, as a walk starts. A change to a
  // Lotus box there changes `canvasFrames`, which starts a new walk.
  const readCanvas = useRecoilCallback(
    ({ snapshot }) =>
      (): BleedCheckCanvas => ({
        pageId: snapshot.getLoadable(canvasPageIdAtom).getValue(),
        items: snapshot
          .getLoadable(itemIdAtom)
          .getValue()
          .map((id) => snapshot.getLoadable(itemAtomFamily(id)).getValue()),
      }),
    [],
  )

  // A callback's snapshot is the committed tree, so a bundle set a moment
  // ago is not in it until React commits: the bundle travels by value from
  // the load to the read, and only the shared inputs come from the snapshot.
  const readPageDocuments = useRecoilCallback(
    ({ snapshot }) =>
      async (
        pageId: string,
        bundle: EditorPageContentBundle,
        canvasItems: CanvasItems,
      ): Promise<EditorTextBoxDocument[]> => {
        const [inputs, drafts, pendingDeletes] = await Promise.all([
          snapshot.getPromise(editorTextBoxResolveInputsSelector),
          snapshot.getPromise(editorTextBoxDraftsAtom),
          snapshot.getPromise(editorTextBoxPendingDeletesAtom),
        ])
        const saved = new Map<string, EditorTextBoxDocument>()
        for (const [boxId, viewModel] of editorTextBoxViewModelResults(
          bundle,
          inputs,
        ))
          if (viewModel.document) saved.set(boxId, viewModel.document)
        return bleedCheckPageDocuments({
          pageId,
          saved,
          drafts,
          pendingDeletes,
          canvasItems,
        })
      },
    [],
  )

  useEffect(() => {
    const judgeNothing = (completed: boolean) => {
      setWarnings(NO_WARNINGS)
      setBleedWarnings(NO_WARNINGS)
      setProcessedCount(0)
      setTotalCount(0)
      setBleedCheckCompleted(completed)
    }
    const pages = Array.isArray(editorPages) ? editorPages : []
    // While the editor loads, no walk has run over the loaded pages, so the
    // check has not finished. `enabled` is off until the loader is done, but
    // the page list can land a render after it; a product always has a page,
    // so an empty list is a load still landing.
    if (!enabled || pages.length === 0) {
      judgeNothing(false)
      return
    }
    const canvas = readCanvas()
    const selection = bleedCheckPages(pages, drafts, canvas)
    // After the load, no margins means the product has no band. A check over
    // such a product has finished with nothing to judge, as has one over a
    // product that holds no Lotus box, drafts and the canvas included; it
    // reports the empty verdict the way a finished walk reports its warnings.
    if (!bleeds || selection.boxCount === 0) {
      judgeNothing(true)
      countEditorProbeRender('bleedCheck:complete')
      return
    }

    let isCancelled = false
    setIsProcessing(true)
    setBleedCheckCompleted(false)
    setProcessedCount(0)
    setTotalCount(selection.boxCount)

    const processAllItems = async () => {
      const newWarnings: BleedWarningInfo[] = []
      let processed = 0

      for (const entry of selection.pages) {
        if (isCancelled) break
        const bundle = await loadWithOneRetry(loadPageBundle, entry.pageId)
        if (isCancelled) break
        // A page the walk could not read leaves the product without a
        // verdict: the check stays unfinished until the next walk.
        if (bundle === null) {
          setIsProcessing(false)
          return
        }
        try {
          const documents = await readPageDocuments(
            entry.pageId,
            bundle,
            entry.pageId === canvas.pageId ? canvas.items : [],
          )
          for (const document of documents) {
            const ink = await measureEditorTextBoxInkRect(document)
            if (isCancelled) break
            const rect = editorTextBoxCheckRect(document, ink)
            const warning = rect
              ? bleedWarningForRect(
                  document.box.boxId,
                  EDITOR_TEXT_BOX_ITEM_TYPE,
                  rect,
                  entry.pageIndex,
                  entry.page,
                  bleeds,
                  layout,
                )
              : null
            if (warning) newWarnings.push(warning)
            processed += 1
            setProcessedCount(processed)
          }
        } catch (error) {
          console.warn(
            `Failed to check bleed for the text boxes of page ${entry.pageId}:`,
            error,
          )
        }
      }

      if (!isCancelled) {
        setWarnings(newWarnings)
        setBleedWarnings(newWarnings)
        setIsProcessing(false)
        setBleedCheckCompleted(true)
        // Probe marks: a spec waits for the check to have judged a box on a
        // page the canvas is not showing before it presses Back.
        for (const warning of newWarnings)
          countEditorProbeRender(
            `bleedCheck:${warning.itemId}:${warning.pageNumber}:${warning.location}`,
          )
        countEditorProbeRender('bleedCheck:complete')
      }
    }

    const timeoutId = setTimeout(processAllItems, debounceMs)

    return () => {
      isCancelled = true
      clearTimeout(timeoutId)
      setIsProcessing(false)
    }
  }, [
    editorPages,
    drafts,
    enabled,
    bleeds,
    layout,
    debounceMs,
    recheckTrigger,
    pendingDeletes,
    canvasFrames,
    readCanvas,
    loadPageBundle,
    readPageDocuments,
    setBleedWarnings,
    setBleedCheckCompleted,
  ])

  return {
    warnings,
    isProcessing,
    processedCount,
    totalCount,
    progressPercentage:
      totalCount > 0 ? Math.round((processedCount / totalCount) * 100) : 0,
    recheckAll,
  }
}
